/**
 * Phase 22.6 — Google Business Profile connection for one client.
 *
 * Lives on Clients, beside Search Console, because platform connections are canonical
 * there (CLAUDE.md, Product Coherence). Self-contained — its own status fetch, popup
 * listener and location picker — so Clients.js does not grow another 300 lines.
 *
 * CONSENT DOES NOT CONNECT. The Google account is Anthony's, a Manager on several
 * listings, so the callback returns the list and one has to be picked. That pick has a
 * deadline (default 15 minutes), which is why a pending consent shows a **Finish
 * connecting** state here rather than looking like a failed attempt: the tokens are
 * already stored and the only thing missing is the choice.
 *
 * WHAT THE NUMBERS ARE. "Reviews on Google" is what Google reports for the listing, next
 * to what Nexus has stored. They should match; when they do not, the difference is real
 * (a review removed since the last read) and shown rather than reconciled away.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

import { gbpAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const LABEL = { color: '#7d8590', mb: 0.5 };
const VALUE = { color: '#f0f6fc', mb: 1.5 };

function formatWhen(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function GbpConnectionCard({ clientId, onChanged }) {
  const { showError, showSuccess } = useToast();
  const [status, setStatus] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (clientId == null) return;
    try {
      setStatus(await gbpAPI.getConnection(clientId));
    } catch (e) {
      // Say it could not be read. A blank card reads as "not connected", which is a
      // different thing and would invite a needless reconnect.
      setStatus({
        error: e?.response?.data?.detail || e?.message || 'Could not read the connection',
      });
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const openPicker = useCallback(
    async (fromPopup) => {
      if (fromPopup) {
        setLocations(fromPopup);
        setPickerOpen(true);
        return;
      }
      setBusy(true);
      try {
        const data = await gbpAPI.getPendingLocations(clientId);
        setLocations(data.locations || []);
        setPickerOpen(true);
      } catch (e) {
        showError(
          e?.response?.data?.detail ||
            'That consent has expired. Connect again to pick a listing.'
        );
        load();
      } finally {
        setBusy(false);
      }
    },
    [clientId, load, showError]
  );

  useEffect(() => {
    function onMessage(event) {
      const d = event.data || {};
      if (d.client_id != null && Number(d.client_id) !== Number(clientId)) return;
      if (d.type === 'gbp_needs_location') {
        openPicker(Array.isArray(d.locations) ? d.locations : []);
        load();
      } else if (d.type === 'gbp_error') {
        showError(d.detail || d.error || 'Business Profile connection failed');
        load();
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [clientId, load, openPicker, showError]);

  const connect = async () => {
    try {
      const url = await gbpAPI.getLoginUrl(clientId);
      window.open(url, '_blank', 'width=600,height=720');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e?.message || 'Could not start Google sign-in'
      );
    }
  };

  const pick = async (locationName) => {
    setBusy(true);
    try {
      const out = await gbpAPI.selectLocation(clientId, locationName);
      setPickerOpen(false);
      showSuccess(
        out.sync_queued
          ? `Connected ${out.location_title}. First review sync queued.`
          : `Connected ${out.location_title}.`
      );
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || 'Could not connect that listing');
    } finally {
      setBusy(false);
    }
  };

  const syncNow = async () => {
    try {
      await gbpAPI.syncNow(clientId);
      showSuccess('Review sync queued. It runs in the background.');
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || 'Could not queue the sync');
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await gbpAPI.disconnect(clientId);
      showSuccess('Business Profile disconnected for this client.');
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      showError(e?.response?.data?.detail || e?.message || 'Could not disconnect');
    } finally {
      setBusy(false);
    }
  };

  const drift =
    status?.connected &&
    status.google_reported_total != null &&
    status.stored_reviews != null &&
    status.google_reported_total !== status.stored_reviews;

  return (
    <Card sx={{ bgcolor: '#21262d', border: '1px solid #30363d', width: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ color: '#f0f6fc', mb: 2 }}>
          Google Business Profile
        </Typography>

        {!status ? (
          <Box sx={{ py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : status.error ? (
          <Alert severity="error">{status.error}</Alert>
        ) : status.pending_location_pick && !status.connected ? (
          <Box>
            <Chip label="Waiting for a listing" color="warning" size="small" sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ color: '#7d8590', mb: 2 }}>
              Google sign-in is done, but no listing is chosen yet, so nothing is being
              read. Pick one before {formatWhen(status.pending_expires_at)} or the consent
              is discarded and you will need to sign in again.
            </Typography>
            <Button
              variant="contained"
              size="large"
              disabled={busy}
              onClick={() => openPicker(null)}
              sx={{ textTransform: 'none', minHeight: 44, bgcolor: '#1a73e8', '&:hover': { bgcolor: '#1557b0' } }}
            >
              Finish connecting
            </Button>
          </Box>
        ) : !status.connected ? (
          <Box>
            <Typography variant="body2" sx={{ color: '#7d8590', mb: 2 }}>
              Connect this client&apos;s Google listing to track how many reviews it
              receives. Sign in as an owner or manager of the listing; you will then pick
              which listing belongs to this client.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={connect}
              sx={{
                bgcolor: '#1a73e8',
                '&:hover': { bgcolor: '#1557b0' },
                textTransform: 'none',
                minHeight: 44,
                maxWidth: { sm: 320 },
              }}
            >
              Connect Google Business Profile
            </Button>
          </Box>
        ) : (
          <Box>
            <Chip label="Connected" color="success" size="small" sx={{ mb: 2 }} />

            <Typography variant="body2" sx={LABEL}>Listing</Typography>
            <Typography variant="body1" sx={VALUE}>
              {status.location_title || status.location_name || '—'}
            </Typography>

            {status.connected_google_email && (
              <>
                <Typography variant="body2" sx={LABEL}>Google account</Typography>
                <Typography variant="body1" sx={VALUE}>
                  {status.connected_google_email}
                </Typography>
              </>
            )}

            <Typography variant="body2" sx={LABEL}>Reviews on Google</Typography>
            <Typography variant="body1" sx={VALUE}>
              {status.google_reported_total == null
                ? '—'
                : `${status.google_reported_total}${
                    status.google_reported_average_rating
                      ? ` · ${status.google_reported_average_rating.toFixed(1)} average`
                      : ''
                  }`}
            </Typography>

            <Typography variant="body2" sx={LABEL}>Last sync</Typography>
            <Typography variant="body1" sx={{ ...VALUE, mb: 2 }}>
              {formatWhen(status.last_sync_at)}
              {status.last_sync_at ? '' : ' — the nightly sync runs at 02:50 UTC'}
            </Typography>

            {drift && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Google reports {status.google_reported_total} reviews and Nexus has{' '}
                {status.stored_reviews} stored. Reviews Google removes are kept in the
                history rather than deleted, so the two can differ.
              </Alert>
            )}

            {status.last_error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Last sync failed: {String(status.last_error).slice(0, 500)}
                {status.last_error_at ? ` (${formatWhen(status.last_error_at)})` : ''}
                . Review counts are as of the last successful sync, not zero.
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={connect}
                sx={{ textTransform: 'none', minHeight: 44, bgcolor: '#1a73e8', '&:hover': { bgcolor: '#1557b0' } }}
              >
                Reconnect
              </Button>
              <Button variant="outlined" size="large" sx={{ textTransform: 'none', minHeight: 44 }} onClick={syncNow}>
                Sync now
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="large"
                disabled={busy}
                sx={{ textTransform: 'none', minHeight: 44 }}
                onClick={disconnect}
              >
                Disconnect
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: '#7d8590', display: 'block', mt: 1.5 }}>
              Disconnecting stops Nexus reading this listing. It does not remove access at
              Google — that consent is shared with this client&apos;s other Google
              connections.
            </Typography>
          </Box>
        )}
      </CardContent>

      <Dialog open={pickerOpen} onClose={() => !busy && setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Which listing belongs to this client?</DialogTitle>
        <DialogContent dividers>
          {locations.length === 0 ? (
            <Typography variant="body2">
              This Google account does not manage any listings.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Every listing this Google account manages is shown. Only the one you pick
                is read.
              </Typography>
              <List dense>
                {locations.map((loc) => (
                  <ListItemButton
                    key={loc.location_name}
                    disabled={busy}
                    onClick={() => pick(loc.location_name)}
                  >
                    <ListItemText
                      primary={loc.title || loc.location_name}
                      secondary={loc.address || loc.location_name}
                    />
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setPickerOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
